import { Controller } from "@hotwired/stimulus"

/**
 * Ticket category picker split into Category (roots) + Subcategory (the
 * chosen root's children) that submits ONE category_id — the subcategory when
 * one is chosen, else the root — through a hidden field (CONEK-7977).
 *
 * Markup and the initial (server-rendered) state come from
 * Support::CategoryPickerComponent; this keeps them in step as the user picks:
 * - Category lists roots, but a search there also finds subcategories under
 *   their "Parent › Child" label; picking one sets Category to the parent and
 *   Subcategory to the child.
 * - Changing Category rebuilds (and clears) Subcategory, which is hidden when
 *   the root has no children. Subcategory is optional.
 *
 * Both dropdowns are Ui::SelectComponent (ui--select / Tom Select). Their
 * instances are read off each native <select> (`select.tomselect`), and
 * every programmatic setValue is silent so it never re-enters these handlers.
 *
 * data-controller="support--category-picker"
 */
export default class extends Controller {
  static targets = ["value", "category", "subcategory"]
  static values = { categories: Array }

  // The control's text input is typed into (input) or focused on open
  // (focusin). Tom Select also fires `input` on the native <select> when the
  // value changes; that one is not a search.
  search(event) {
    if (event.target.tagName !== "INPUT") return

    this.#listSubcategoriesInCategory(event.target.value.trim() !== "")
  }

  pickCategory() {
    const category = this.#tomSelect(this.categoryTarget)
    if (!category) return

    const picked = this.#find(category.getValue())
    const root = picked?.parent_id ? this.#find(picked.parent_id) : picked
    const subcategoryId = picked?.parent_id ? picked.value : ""

    if (root && root !== picked) category.setValue(root.value, true)
    this.#listSubcategoriesInCategory(false)
    this.#fillSubcategory(root?.value ?? "", subcategoryId)
    this.#setSubmittedCategory(subcategoryId || root?.value || "")
  }

  pickSubcategory() {
    const category = this.#tomSelect(this.categoryTarget)
    const subcategory = this.#tomSelect(this.subcategoryTarget)
    if (!category || !subcategory) return

    this.#setSubmittedCategory(subcategory.getValue() || category.getValue())
  }

  #listSubcategoriesInCategory(listed) {
    const category = this.#tomSelect(this.categoryTarget)
    if (!category) return

    for (const child of this.categoriesValue.filter((c) => c.parent_id)) {
      if (listed) {
        category.addOption({ value: child.value, text: child.label })
      } else if (!category.items.includes(child.value)) {
        category.removeOption(child.value, true)
      }
    }
    // A non-empty query is filtered by Tom Select's own debounced refresh,
    // which runs after this and picks the added options up; refreshing here
    // too would let that later refresh reopen a dropdown the user has already
    // picked from. An emptied query is refreshed by Tom Select at once —
    // before this ran — so the removal has to be re-rendered here.
    if (!listed) category.refreshOptions(false)
  }

  #fillSubcategory(rootId, subcategoryId) {
    const subcategory = this.#tomSelect(this.subcategoryTarget)
    if (!subcategory) return

    const children = this.categoriesValue.filter(
      (c) => rootId && c.parent_id === rootId,
    )

    subcategory.clear(true)
    subcategory.clearOptions()
    for (const child of children) {
      subcategory.addOption({ value: child.value, text: child.name })
    }
    subcategory.setValue(subcategoryId, true)
    this.subcategoryTarget.classList.toggle("hidden", children.length === 0)
  }

  #setSubmittedCategory(categoryId) {
    this.valueTarget.value = categoryId
  }

  #find(id) {
    return this.categoriesValue.find((c) => c.value === id)
  }

  // Tom Select's own handle on the <select> it enhances (set by the library,
  // independent of the ui--select controller's internals).
  #tomSelect(wrapper) {
    return wrapper.querySelector("select")?.tomselect ?? null
  }
}
